import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';

const SchemeManagement = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('scheme-management', 'delete');
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    scheme_name: '',
    scheme_type: 'fixed',
    monthly_amount: '',
    duration_months: 12,
    company_bonus_month: 12,
    currency: 'USD',
    description: ''
  });

  // Fetch schemes on mount
  useEffect(() => {
    fetchSchemes();
  }, []);

  const fetchSchemes = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/schemes');
      const result = await response.json();
      
      if (result.success) {
        setSchemes(result.data);
      } else {
        setError(result.error || 'Failed to fetch schemes');
      }
    } catch (err) {
      setError('Error fetching schemes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'monthly_amount' || name === 'duration_months' ? parseFloat(value) || '' : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.scheme_name || !formData.monthly_amount || !formData.duration_months) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.monthly_amount <= 0 || formData.duration_months <= 0) {
      setError('Monthly amount and duration must be greater than 0');
      return;
    }

    if (formData.company_bonus_month > formData.duration_months) {
      setError('Company bonus month cannot exceed scheme duration');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = editingId ? `/api/schemes/${editingId}` : '/api/schemes';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(`Scheme ${editingId ? 'updated' : 'created'} successfully!`);
        fetchSchemes();
        resetForm();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to save scheme');
      }
    } catch (err) {
      setError('Error saving scheme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (scheme) => {
    setFormData({
      scheme_name: scheme.scheme_name,
      scheme_type: scheme.scheme_type,
      monthly_amount: scheme.monthly_amount,
      duration_months: scheme.duration_months,
      company_bonus_month: scheme.company_bonus_month,
      currency: scheme.currency || 'USD',
      description: scheme.description || ''
    });
    setEditingId(scheme.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    if (!window.confirm('Are you sure you want to delete this scheme?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/schemes/${id}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Scheme deleted successfully!');
        fetchSchemes();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to delete scheme');
      }
    } catch (err) {
      setError('Error deleting scheme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      scheme_name: '',
      scheme_type: 'fixed',
      monthly_amount: '',
      duration_months: 12,
      company_bonus_month: 12,
      currency: 'USD',
      description: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const calculateTotal = () => {
    const monthly = parseFloat(formData.monthly_amount) || 0;
    const duration = parseInt(formData.duration_months) || 1;
    return (monthly * duration).toFixed(2);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.MANAGEMENT} pageName="Scheme Management" />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Scheme Management</h1>
          <p className="text-gray-500 mt-1">Create and manage gold monthly installment schemes</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
        >
          <Plus size={20} /> Create New Scheme
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <X size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <Check size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-amber-100">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">
            {editingId ? 'Edit Scheme' : 'Create New Scheme'}
          </h2>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Scheme Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheme Name *
              </label>
              <input
                type="text"
                name="scheme_name"
                value={formData.scheme_name}
                onChange={handleInputChange}
                placeholder="e.g., Gold-500"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Scheme Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheme Type *
              </label>
              <select
                name="scheme_type"
                value={formData.scheme_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="fixed">Fixed Group</option>
                <option value="custom">Custom Individual</option>
              </select>
            </div>

            {/* Monthly Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Amount ({formData.currency}) *
              </label>
              <input
                type="number"
                name="monthly_amount"
                value={formData.monthly_amount}
                onChange={handleInputChange}
                placeholder="500"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (Months) *
              </label>
              <input
                type="number"
                name="duration_months"
                value={formData.duration_months}
                onChange={handleInputChange}
                placeholder="12"
                min="1"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Company Bonus Month */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Pays Month
              </label>
              <input
                type="number"
                name="company_bonus_month"
                value={formData.company_bonus_month}
                onChange={handleInputChange}
                placeholder="12"
                min="1"
                max={formData.duration_months || 12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="USD">USD ($)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Add any additional details about this scheme..."
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Total Amount Display */}
            <div className="md:col-span-2 bg-amber-50 p-4 rounded-lg border border-amber-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Monthly</p>
                  <p className="text-lg font-bold text-amber-600">
                    {formData.currency === 'USD' ? '$' : '₹'}{parseFloat(formData.monthly_amount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="text-lg font-bold text-amber-600">{formData.duration_months} months</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-lg font-bold text-amber-600">
                    {formData.currency === 'USD' ? '$' : '₹'}{calculateTotal()}
                  </p>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                {loading ? 'Saving...' : (editingId ? 'Update Scheme' : 'Create Scheme')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Schemes List */}
      {loading && !showForm ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading schemes...</p>
        </div>
      ) : schemes.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-200">
          <p className="text-gray-600 mb-4">No schemes created yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 transition"
          >
            <Plus size={20} /> Create First Scheme
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schemes.map(scheme => (
            <div
              key={scheme.id}
              className={`bg-white rounded-lg shadow p-6 border-l-4 ${
                scheme.is_active ? 'border-l-green-500' : 'border-l-gray-300 opacity-70'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{scheme.scheme_name}</h3>
                  <p className="text-sm text-gray-500">
                    {scheme.scheme_type === 'fixed' ? 'Fixed Group' : 'Custom Individual'}
                  </p>
                </div>
                {!scheme.is_active && (
                  <span className="bg-gray-200 text-gray-800 text-xs px-2 py-1 rounded">Inactive</span>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monthly Amount:</span>
                  <span className="font-semibold text-amber-600">
                    {scheme.currency === 'USD' ? '$' : '₹'}{parseFloat(scheme.monthly_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold">{scheme.duration_months} months</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-semibold text-amber-600">
                    {scheme.currency === 'USD' ? '$' : '₹'}{parseFloat(scheme.total_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Company Pays Month:</span>
                  <span className="font-semibold">{scheme.company_bonus_month}</span>
                </div>
              </div>

              {scheme.description && (
                <p className="text-sm text-gray-600 mb-4 italic">{scheme.description}</p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(scheme)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-1 transition"
                >
                  <Edit2 size={16} /> Edit
                </button>
                {canDelete && (
                <button
                  onClick={() => handleDelete(scheme.id)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-1 transition"
                >
                  <Trash2 size={16} /> Delete
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchemeManagement;
